import { createSelector } from "reselect"
import {
    createProfileInstanceSelector,
    createSectionObject,
    makeImmutableSliceSelector,
    makeObjSliceSelector,
    createDataSections,
    createToJsSelector,
    createTransactionSummaryColumnMapping,
} from "shared/imports/sharedHelperFunctions"
import { selectLedgerSettingsListIds } from "shared/pac/selectors"
import { COMMITTEES_REQUEST_LIMIT } from "shared/imports/sharedConstants"

export const selectPoliticalCommitteeProfile = (state) => state.politicalCommitteeProfile

export const selectProfileInstance = createProfileInstanceSelector({
    selector: selectPoliticalCommitteeProfile,
    key: "politicalCommittees",
})

const makePoliticalCommitteeProfileSelect = makeImmutableSliceSelector(selectProfileInstance)

export const selectRegion = makePoliticalCommitteeProfileSelect("region") // temp

export const selectPoliticalCommittee = makePoliticalCommitteeProfileSelect("politicalCommittee")
export const selectIsLoadingPoliticalCommittee = makePoliticalCommitteeProfileSelect("isLoadingPoliticalCommittee")
export const selectCandidate = makePoliticalCommitteeProfileSelect("candidate")
export const selectIsLoadingCandidate = makePoliticalCommitteeProfileSelect("isLoadingCandidate")

export const selectIsLoadingAffiliatedCommittees = makePoliticalCommitteeProfileSelect("isLoadingAffiliatedCommittees")
export const selectAffiliatedCommittees = makePoliticalCommitteeProfileSelect("affiliatedCommittees")
export const selectNumAffiliatedCommittees = createSelector(
    selectAffiliatedCommittees,
    (affiliatedCommittees) => affiliatedCommittees && affiliatedCommittees.length,
)
//
export const selectAffiliatedCommitteesSectionTitle = createSelector(selectNumAffiliatedCommittees, (numCommittees) =>
    numCommittees < COMMITTEES_REQUEST_LIMIT
        ? `Affiliated Committees (${numCommittees})`
        : `Affiliated Committees (${COMMITTEES_REQUEST_LIMIT}+)`,
)

export const selectIsLoadingJointFundraisingCommittees = makePoliticalCommitteeProfileSelect(
    "isLoadingJointFundraisingCommittees",
)
export const selectJointFundraisingCommittees = makePoliticalCommitteeProfileSelect("jointFundraisingCommittees")
export const selectNumJointFundraisingCommittees = createSelector(
    selectJointFundraisingCommittees,
    (jointFundraisingCommittees) => jointFundraisingCommittees && jointFundraisingCommittees.length,
)
//
export const selectJointFundraisingCommitteesSectionTitle = createSelector(
    selectNumJointFundraisingCommittees,
    (numCommittees) =>
        numCommittees < COMMITTEES_REQUEST_LIMIT
            ? `Joint Fundraising Participants (${numCommittees})`
            : `Joint Fundraising Participants (${COMMITTEES_REQUEST_LIMIT}+)`,
)

export const selectIsLoadingJointFundraisingRepresentatives = makePoliticalCommitteeProfileSelect(
    "isLoadingJointFundraisingRepresentatives",
)
export const selectJointFundraisingRepresentatives = makePoliticalCommitteeProfileSelect(
    "jointFundraisingRepresentatives",
)
export const selectNumJointFundraisingRepresentatives = createSelector(
    selectJointFundraisingRepresentatives,
    (jointFundraisingRepresentatives) => jointFundraisingRepresentatives && jointFundraisingRepresentatives.length,
)

export const selectJointFundraisingRepresentativesSectionTitle = createSelector(
    selectNumJointFundraisingRepresentatives,
    (numCommittees) =>
        numCommittees < COMMITTEES_REQUEST_LIMIT
            ? `Joint Fundraising Representatives (${numCommittees})`
            : `Joint Fundraising Representatives (${COMMITTEES_REQUEST_LIMIT}+)`,
)

export const selectIsLoadingPoliticalCommitteeProfile = createSelector(
    selectIsLoadingPoliticalCommittee,
    selectIsLoadingAffiliatedCommittees,
    (isLoadingPoliticalCommittee, isLoadingAffiliatedCommittees) =>
        isLoadingPoliticalCommittee || isLoadingAffiliatedCommittees,
)

const makePoliticalCommitteeSelect = makeObjSliceSelector(selectPoliticalCommittee)

export const selectTitle = makePoliticalCommitteeSelect("name")
export const selectCommitteeId = makePoliticalCommitteeSelect("fec_committee_id")
export const selectFecCommitteeDesignation = makePoliticalCommitteeSelect("fec_committee_designation")
export const selectCandidateStatus = makePoliticalCommitteeSelect("candidate_status")
export const selectIsActive = makePoliticalCommitteeSelect("is_active")

export const selectCommitteeType = makePoliticalCommitteeSelect("committee_type")
export const selectCommitteeTypeLabel = createSelector(
    selectCommitteeType,
    (committeeType) => committeeType && DjangIO.app.pac.types.PoliticalCommitteeType.by_value(committeeType).label,
)

export const selectIsCandidateCommittee = createSelector(
    selectCommitteeType,
    (committeeType) => committeeType === DjangIO.app.pac.types.PoliticalCommitteeType.candidate_committee.value,
)

export const selectCommitteeDesignationLabel = createSelector(
    selectFecCommitteeDesignation,
    (committeeDesignation) =>
        committeeDesignation && DjangIO.app.pac.types.FecCommitteeDesignation.by_value(committeeDesignation).label,
)

const selectCandidateStatusLabel = createSelector(
    selectCandidateStatus,
    (candidateStatus) => candidateStatus && DjangIO.app.pac.types.FecCandidateStatus.by_value(candidateStatus).label,
)

export const selectParty = makePoliticalCommitteeSelect("party")
export const selectPartyLabel = createSelector(
    selectParty,
    (party) => party && DjangIO.app.pac.types.FecParty.by_value(party).label,
)

export const selectCandidateName = makePoliticalCommitteeSelect("candidate_name")
export const selectCandidateId = makePoliticalCommitteeSelect("candidate_id")
export const selectCandidateImageUrl = makePoliticalCommitteeSelect("candidate_image_url")
export const selectCandidateCommitteeString = makePoliticalCommitteeSelect("candidate_committee_string")
export const selectCandidateOffice = makePoliticalCommitteeSelect("candidate_office_string")

export const selectCandidateOfficeState = makePoliticalCommitteeSelect("candidate_state_region")
export const selectCandidateOfficeStateLabel = createSelector(
    selectCandidateOfficeState,
    (state) => state && DjangIO.app.models.Region.by_value(state).abbrev.toUpperCase(),
)

export const selectCandidateUrl = createSelector(
    selectCandidateId,
    (candidateId) => candidateId && `/official/${candidateId}/`,
)

export const selectDistrictName = makePoliticalCommitteeSelect("district_name")
export const selectElectionYear = makePoliticalCommitteeSelect("election_year")

export const selectAddressStreet1 = makePoliticalCommitteeSelect("addr_street1")
export const selectAddressStreet2 = makePoliticalCommitteeSelect("addr_street2")
export const selectAddressCity = makePoliticalCommitteeSelect("addr_city")
export const selectAddressState = makePoliticalCommitteeSelect("addr_state")
export const selectAddressZip = makePoliticalCommitteeSelect("addr_zip")

export const selectOrganization = makePoliticalCommitteeSelect("organization")

export const selectFormattedZip = createSelector(selectAddressZip, (zip) =>
    zip && zip.length > 5 ? `${zip.substr(0, 5)}-${zip.substr(5)}` : zip,
)

/**
 * selectFullAddress formats the full address of the Political Committee
 * If the main street, city, or state is not provided, the address
 * will be considered invalid and not displayed on the profile
 *
 * @name selectFullAddress
 * @function
 * @param {Object} street1 - 1111 19th St
 * @param {Object} street2 - Building X
 * @param {Object} city - WASHINGTON
 * @param {Object} addressState - DC
 * @param {Object} zip - 12345
 */
export const selectFullAddress = createSelector(
    selectAddressStreet1,
    selectAddressStreet2,
    selectAddressCity,
    selectAddressState,
    selectFormattedZip,
    (street1, street2, city, addressState, zip) => {
        if (!street1 || !city || !addressState) {
            return undefined
        }
        const street = street2 ? `${street1} ${street2}` : street1
        return `${street}, ${city}, ${addressState} ${zip}`
    },
)

export const selectIsPoliticalCommitteeLoading = makePoliticalCommitteeProfileSelect("isLoadingPoliticalCommittee")
export const selectId = makePoliticalCommitteeSelect("id")
export const selectImmutableTransactionSummary = makePoliticalCommitteeProfileSelect("transactionSummary")
export const selectTransactionSummary = createToJsSelector(selectImmutableTransactionSummary)
export const selectIsLoadingTransactionSummary = makePoliticalCommitteeProfileSelect("isLoadingTransactionSummary")
export const selectFetchTransactionSummaryFailed = makePoliticalCommitteeProfileSelect("fetchTransactionSummaryFailed")

export const selectShouldLoadTransactionSummary = createSelector(
    selectIsLoadingTransactionSummary,
    selectFetchTransactionSummaryFailed,
    selectTransactionSummary,
    selectLedgerSettingsListIds,
    selectId,
    (isLoading, fetchFailed, summary, ledgerSettingsListIds, id) =>
        !isLoading &&
        !fetchFailed &&
        !summary &&
        ledgerSettingsListIds &&
        Boolean(ledgerSettingsListIds[0]) &&
        Boolean(id),
)

export const selectTransactionSummaryColumnMapping = createSelector(
    selectTransactionSummary,
    selectCommitteeType,
    (transactionSummary, committeeType) =>
        createTransactionSummaryColumnMapping({
            transactionSummary,
            qdt: DjangIO.app.models.QuorumDataType.political_committee.value,
            committeeType,
        }),
)

export const selectContactCardData = ({ sectionComponent }) =>
    createSelector(
        selectTitle,
        selectFullAddress,
        selectCommitteeTypeLabel,
        selectIsActive,
        (title, address, committeeType, isActive) => {
            const contactData = title
                ? [
                      {
                          detailItems: [
                              { label: "Address", text: address || "N/A" },
                              { label: "Active", text: isActive ? "Yes" : "No" },
                          ],
                          infoItems: [
                              {
                                  text: committeeType,
                                  textType: "secondary",
                              },
                          ],
                          icon: "dollar-sign",
                          name: title,
                      },
                  ]
                : []

            const contactCardObj = createSectionObject({
                title: "Contact Card",
                data: contactData,
                id: "officialContactCard",
                sectionComponent,
            })

            return contactCardObj
        },
    )

/**
 * Load the About section DTV for the profile
 * Per request of Camille, fields Candidate Name, Office, and District
 * should only be loaded if a candidate exists.
 *
 * @name selectAbout
 * @function
 */
export const selectAbout = ({ isMobile, sectionComponent }) =>
    createSelector(
        selectTitle,
        selectCommitteeId,
        selectCommitteeTypeLabel,
        selectPartyLabel,
        selectCommitteeDesignationLabel,
        selectCandidateName,
        selectCandidateOffice,
        selectCandidateOfficeStateLabel,
        selectCandidateStatusLabel,
        selectDistrictName,
        selectElectionYear,
        (
            title,
            committeeId,
            committeeType,
            party,
            committeeDesignation,
            candidateName,
            candidateOffice,
            candidateOfficeState,
            candidateStatus,
            districtName,
            electionYear,
        ) => {
            const aboutData = [
                ...(committeeId
                    ? [
                          {
                              title: "ID",
                              value: committeeId,
                              width: 3,
                          },
                      ]
                    : []),
                ...(candidateName
                    ? [
                          {
                              title: "Candidate Name",
                              value: candidateName,
                              width: 3,
                          },
                      ]
                    : []),
                ...(candidateOffice
                    ? [
                          {
                              title: "Office",
                              value: candidateOffice,
                              width: 3,
                          },
                      ]
                    : []),
                ...(candidateOfficeState
                    ? [
                          {
                              title: "Office State",
                              value: candidateOfficeState,
                              width: 3,
                          },
                      ]
                    : []),
                ...(districtName
                    ? [
                          {
                              title: "District",
                              value: districtName,
                              width: 3,
                          },
                      ]
                    : []),
                ...(party
                    ? [
                          {
                              title: "Party",
                              value: party,
                              width: 3,
                          },
                      ]
                    : []),
                ...(electionYear
                    ? [
                          {
                              title: "Election Year",
                              value: electionYear,
                              width: 3,
                          },
                      ]
                    : []),
                ...(committeeType
                    ? [
                          {
                              title: "Type",
                              value: committeeType,
                              width: 3,
                          },
                      ]
                    : []),
                ...(committeeDesignation
                    ? [
                          {
                              title: "FEC Designation",
                              value: committeeDesignation,
                              width: 3,
                          },
                      ]
                    : []),
                ...(candidateStatus
                    ? [
                          {
                              title: "Candidate Status",
                              value: candidateStatus,
                              width: 3,
                          },
                      ]
                    : []),
            ]

            const infoDataSections = createDataSections(
                !isMobile ? aboutData.filter((dataObj) => !dataObj.isMobile) : aboutData,
            )

            const aboutObj = createSectionObject({
                title: "About",
                data: infoDataSections,
                id: "politicalCommitteeSectionData",
                sectionComponent,
            })

            return aboutObj
        },
    )

export const selectCandidateSection = ({ sectionComponent }) =>
    createSelector(selectId, (id) =>
        createSectionObject({
            title: "Official",
            data: [{ id }],
            sectionComponent,
        }),
    )

export const selectAffiliatedCommitteeSection = ({ sectionComponent }) =>
    createSelector(selectId, selectAffiliatedCommitteesSectionTitle, (id, title) =>
        createSectionObject({
            title,
            data: [{ id }],
            sectionComponent,
        }),
    )

export const selectJointFundraisingCommitteeSection = ({ sectionComponent }) =>
    createSelector(selectId, selectJointFundraisingCommitteesSectionTitle, (id, title) =>
        createSectionObject({
            title,
            data: [{ id }],
            sectionComponent,
        }),
    )

export const selectJointFundraisingRepresentativeSection = ({ sectionComponent }) =>
    createSelector(selectId, selectJointFundraisingRepresentativesSectionTitle, (id, title) =>
        createSectionObject({
            title,
            data: [{ id }],
            sectionComponent,
        }),
    )

export const selectTableSection = ({ sectionComponent }) =>
    createSelector(selectId, (id) =>
        createSectionObject({
            title: "Table",
            data: [{ id }],
            sectionComponent,
        }),
    )
